import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, BoxProps } from '@mui/material';

import { DEBT_PERCENT_VOLATILITY_THRESHOLDS } from '@arrived/common';

import { SUPPORTED_RICH_TEXT_FEATURES } from '../../../RichTextSupport';

export interface VolatilityContentProps extends BoxProps {
  debtPercent: number;
}

const LowerVolatilityFormattedMessage = () => (
  <FormattedMessage
    id="lower-volatility-explanation"
    defaultMessage="Relative to other properties on the Arrived platform, this property may carry <strong>lower</strong> potential for equity returns, but also carries <strong>lower</strong> potential for volatility given the amount of leverage used on the property."
    values={SUPPORTED_RICH_TEXT_FEATURES}
  />
);

const AverageVolatilityFormattedMessage = () => (
  <FormattedMessage
    id="average-volatility-explanation"
    defaultMessage="Relative to comparable properties on the Arrived platform, this property may carry <strong>average</strong> potential for equity returns, but also carries <strong>average</strong> potential for volatility given the amount of leverage used on the property."
    values={SUPPORTED_RICH_TEXT_FEATURES}
  />
);

const HighVolatilityFormattedMessage = () => (
  <FormattedMessage
    id="high-volatility-explanation"
    defaultMessage="Relative to comparable properties on the Arrived platform, this property may carry <strong>higher</strong> potential for equity returns than similar rental homes with no leverage, but also carries <strong>higher</strong> potential for volatility given the amount of leverage used on the property."
    values={SUPPORTED_RICH_TEXT_FEATURES}
  />
);

export const VolatilityContent = ({ debtPercent, ...rest }: VolatilityContentProps) => {
  const Content = useCallback(() => {
    if (debtPercent < DEBT_PERCENT_VOLATILITY_THRESHOLDS.LOW) {
      return <LowerVolatilityFormattedMessage />;
    } else if (debtPercent < DEBT_PERCENT_VOLATILITY_THRESHOLDS.AVERAGE) {
      return <AverageVolatilityFormattedMessage />;
    } else {
      return <HighVolatilityFormattedMessage />;
    }
  }, [debtPercent]);

  return (
    <Box {...rest}>
      <Content />
    </Box>
  );
};
